<template>
  <BContainer
    class="px-0"
    fluid
  >

    <BContainer
      v-if="showUploader"
      class="px-0"
      fluid
    >
      <ValidationObserver
        ref="formDocument"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="attachments"
                name="attachments"
                rules="required|max_attachment:10"
              >
                <BFormGroup
                  label="Attachments"
                  label-for="fAttachments"
                  label-class="font-weight-bolder"
                  description="Max of 10 files can be attached per upload for optimal compression and upload performance"
                >
                  <BFormFile
                    id="fAttachments"
                    v-model="attachment.list"
                    :accept="accept"
                    :multiple="multiple"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
            <BCol
              v-if="showProgress && upload.progress > 0"
              cols="12"
              class="pb-2"
            >
              <BProgress
                :value="upload.progress"
                striped
                animated
                show-progress
                variant="primary"
              />
            </BCol>
            <BCol
              v-if="showProgress && fileUnderCompress > 0"
              cols="12"
              class="pb-2"
            >
              <BProgress
                striped
                animated
                show-progress
                variant="primary"
                :value="fileProgressCompress"
              />
              {{ canUseWebWorker ? 'Background' : '' }} Compressing Attached Files
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <div class="d-flex justify-content-between border-bottom pb-3 mb-4">
        <div>
          <strong>Uploaded</strong> - {{ uploadedFiles.length }} Files
        </div>
        <div class="d-flex gap-2">
          <BButton
            size="sm"
            :variant="isAllowedToUploadFile ? 'outline-primary' : 'primary'"
            :disabled="isAllowedToUploadFile || busy"
            @click="uploadDocuments"
          >
            Upload
          </BButton>
          <BButton
            size="sm"
            :variant="uploadedFiles.length <= 0 ? 'outline-danger' : 'danger'"
            :disabled="uploadedFiles.length <= 0 || busy"
            @click="deleteAllUploaded"
          >
            Delete All
          </BButton>
        </div>
      </div>
    </BContainer>

    <BContainer
      v-else-if="!showUploader && uploadedFiles.length <= 0"
      class="px-0"
      fluid
    >
      <div class="d-flex flex-column justify-content-center align-items-center min-h-300px">
        <BImg
          :src="getInvalidImageUrl"
          width="250px"
        />
        NO ATTACHMENTS
      </div>
    </BContainer>

    <BRow>
      <BCol
        v-for="(file, key) in uploadedFiles"
        :key="key"
        class="mb-2"
        cols="12"
        md="4"
      >
        <div
          class="d-flex flex-row border rounded h-100 attachment-background"
        >
          <div
            class="p-2"
          >
            <BImg
              v-if="busy"
              :src="getInvalidImageUrl"
              class="attachment"
            />
            <BImg
              v-else-if="file.file_extension === 'pdf'"
              :src="getPdfImageUrl"
              class="attachment"
            />
            <BImg
              v-else
              :src="uploadedDocumentEndpoint(file.file_hashed_name)"
              :blank-src="getInvalidImageUrl"
              :alt="file.file_original_name"
              class="attachment"
              @error="({ target }) => target.src = getInvalidImageUrl"
            />
          </div>
          <div class="d-flex flex-column justify-content-between w-100 h-100 p-2">
            <div class="d-flex flex-column">
              <div class="text-break font-weight-bolder">
                {{ file.file_original_name }}
              </div>
              <div style="font-size: 12px;">
                {{ Number(file.file_size / 1000).toFixed(2) }} KB
              </div>
            </div>
            <div class="align-self-end">
              <BButton
                size="sm"
                variant="primary"
                @click="previewDocument(file, key)"
              >
                View
              </BButton>
              <BButton
                v-if="showUploader"
                size="sm"
                variant="outline-danger"
                class="ml-1"
                @click="deleteUploaded(file, key)"
              >
                Delete
              </BButton>
            </div>
          </div>
        </div>
      </BCol>
    </BRow>

    <BModal
      ref="modalDocuments"
      size="xl"
      scrollable
      hide-header
      aria-hidden="false"
      no-close-on-backdrop
      modal-class="fullscreen"
    >
      <BCarousel
        id="carouselDocuments"
        v-model="carousel"
        controls
        indicators
        no-touch
        class="h-100"
        background="#aaa"
        :interval="1000000"
      >
        <BCarouselSlide
          v-for="(file, key) in uploadedFiles"
          :key="`uploaded-${key}`"
          caption-tag="h6"
          :caption="file.file_original_name"
          :text="`${Number(file.file_size / 1000).toFixed(2)} KB`"
          style="min-height: calc(100vh - 85px);"
        >
          <template #img>
            <div
              v-if="file.file_extension === 'pdf'"
              class="d-flex justify-content-center align-items-center"
            >
              <iframe
                v-if="carousel === key"
                :src="uploadedDocumentEndpoint(file.file_hashed_name)"
                @error="invalidAttachment"
              />
            </div>
            <div
              v-else
              class="d-flex justify-content-center align-items-center"
            >
              <panZoom
                :options="{
                  minZoom: 0.5,
                  maxZoom: 5,
                  bounds: true,
                  boundsPadding: 0.5
                }"
              >
                <BImg
                  class="d-blocks"
                  :src="uploadedDocumentEndpoint(file.file_hashed_name)"
                />
              </panZoom>
            </div>
          </template>
        </BCarouselSlide>
      </BCarousel>
      <template #modal-footer="{ cancel, visible }">
        <div class="col-12 d-flex justify-content-between px-0 pb-0 mb-0">
          <div />
          <div>
            <BButton
              v-if="visible"
              variant="outline-dark"
              :disabled="busy"
              @click="cancel()"
            >
              Close <span class="d-none d-md-inline-block">Preview</span>
            </BButton>
          </div>
        </div>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import { remove } from 'lodash'
import { SSharedAttachment } from '@/services'

export default {
  name: 'VCAttachment',

  mixins: [
    MISC
  ],

  emits: [
    'files:updated'
  ],

  props: {
    accept: {
      type: String,
      default: () => 'image/jpg, image/jpeg, image/png, application/pdf' // 'image/*' // 'image/jpg, image/jpeg, image/png', application/pdf
    },
    multiple: {
      type: Boolean,
      default: () => true
    },
    showProgress: {
      type: Boolean,
      default: () => true
    },
    showUploader: {
      type: Boolean,
      default: () => true
    },
    showCompressed: {
      type: Boolean,
      default: () => false
    },
    path: {
      type: String,
      default: () => ''
    },
    files: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      canUseWebWorker: typeof Worker === 'function',
      preview: {
        compress: true
      },
      busy: false,
      carousel: 0,
      upload: {
        progress: 0,
        loaded: 0,
        total: 0
      },
      attachment: {
        list: [],
        path: this.path
      },
      invalidFiles: [],
      compressingFiles: [],
      compressedFiles: [],
      uploadedFiles: this.files
    }
  },

  computed: {

    getInvalidImageUrl () {
      return `${window.location.origin}/img/invalid-image.png`
    },

    getPdfImageUrl () {
      return `${window.location.origin}/img/pdf.png`
    },

    fileProgressCompress () {
      const totalProgress = this.compressingFiles.map(compressor => compressor.progress).reduce(
        (total, amount) => Number(total) + Number(amount), 0
      ) / 100

      return totalProgress / this.compressingFiles.length * 100
    },

    fileUnderCompress () {
      return this.compressingFiles.filter(file => file.compressing).length
    },

    isAllowedToUploadFile () {
      return this.fileUnderCompress > 0 || this.compressedFiles.length <= 0 || this.attachment.list.length > 10
    }
  },

  watch: {
    'attachment.list' (files) {
      if (!this.showUploader) {
        this.compressFiles = []
      }

      this.compressFiles({ target: { files } })
    }
  },

  methods: {

    invalidAttachment ({ target }) {
      target.src = require('@/assets/images/invalid-image.png')
    },

    toBaseUrl (file) {
      window.URL = window.URL || window.webkitURL
      return window.URL.createObjectURL(file)
    },

    removeWhiteSpace (string) {
      return String(string).toLowerCase().replace(/\s/g, '')
    },

    async checkInvalidFiles () {
      const isValidFile = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']

      this.compressedFiles = []

      this.invalidFiles = remove(this.attachment.list, file => {
        const fileName = this.removeWhiteSpace(file.name)

        const isAlreadyCompressed = Array.from(this.compressedFiles).some(
          compressedFile => fileName === this.removeWhiteSpace(compressedFile.name)
        )

        const isAlreadyUploaded = Array.from(this.uploadedFiles).some(
          uploadedFile => fileName === this.removeWhiteSpace(uploadedFile.file_original_name)
        )

        const isInvalidFileType = !isValidFile.includes(file.type)

        return isInvalidFileType || isAlreadyCompressed || isAlreadyUploaded
      })
    },

    async compressFiles ({ target }) {
      if (Array.from(target.files).length > 10 || Array.from(target.files).length <= 0) {
        return ''
      }

      this.checkInvalidFiles()

      this.compressingFiles = Array.from(this.attachment.list).map(
        file => ({
          file,
          progress: 0,
          compressed: false,
          compressing: false
        })
      )

      const batchSize = 2
      const batches = []

      for (let i = 0; i < this.compressingFiles.length; i += batchSize) {
        batches.push(this.compressingFiles.slice(i, i + batchSize))
      }

      const processBatch = batch => {
        return Promise.allSettled(
          batch.map(compressor => {
            compressor.compressing = true

            compressor.compressor = this.compressFile(
              compressor.file,
              progress => {
                compressor.progress = progress
              }
            ).then(
              file => {
                compressor.compressed = true
                compressor.progress = 100

                this.compressedFiles.push(file)
                return file
              }
            ).catch(
              () => {
                compressor.compressed = false
                compressor.progress = 0

                this.invalidFiles.push(compressor.file)
                return null
              }
            ).finally(() => {
              compressor.compressing = false
            })

            return compressor.compressor
          })
        )
      }

      let chain = Promise.resolve()

      batches.forEach(batch => {
        chain = chain.then(() => processBatch(batch))
      })

      chain.then(() => {
        this.busy = false
      }).catch(() => {
        this.swalInvalid('<h6>Unable to compress files, please try again</h6>')
      })
    },

    uploadedDocumentEndpoint (fileName) {
      if (this.showUploader) {
        return `${process.env.VUE_APP_API_BASE_URL}/payment-requests-uploads/${this.attachment.path}/${fileName}`
      }

      return `${process.env.VUE_APP_API_BASE_URL}/payment-requests-attachments/${this.attachment.path}/${fileName}`
    },

    async uploadDocuments () {
      this.$refs.formDocument.validate().then(
        async validDocument => {
          if (!validDocument) {
            return this.swalInvalid()
          }

          this.busy = true

          const formDocument = new Promise(resolve => {
            const isDocumentPathValid = [null, 'null', ''].includes(this.attachment.path)

            let totalSize = this.uploadedFiles.map(file => file.file_size).reduce(
              (totalSize, fileSize) => parseFloat(totalSize) + parseFloat(fileSize), 0
            )

            const form = new FormData()
            form.append('attachment_path', isDocumentPathValid ? '' : this.attachment.path)

            this.compressedFiles.forEach(
              (compressedFile, position, lists) => {
                totalSize += parseFloat(compressedFile.size)

                form.append(`file[${position}]`, compressedFile, compressedFile.name)

                if ((Number(position) + 1) === lists.length) {
                  resolve({ form, totalSize })
                }
              }
            )
          })

          return formDocument.then(
            ({ form, totalSize }) => {
              const oneMegaBytes = 1000000
              const errorMessage = 'The attachment total size must be at least lesser than or equal to 25MB'

              return new Promise(resolve => {
                if (parseFloat(totalSize) > parseFloat(oneMegaBytes * 25)) {
                  resolve()
                  return this.$refs.formDocument.setErrors({
                    attachments: [errorMessage]
                  })
                }

                SSharedAttachment.uploadAttachments(
                  form,
                  (progress, loaded, total) => {
                    this.upload.progress = progress
                    this.upload.loaded = loaded
                    this.upload.total = total
                  }
                ).then(
                  ({ data }) => {
                    this.$refs.formDocument.reset()
                    this.attachment.list = this.compressedFiles = this.compressingFiles = []
                    this.attachment.path = data.attachment_path
                    this.uploadedFiles.push(...data.attachments)

                    this.$emit('files:updated', {
                      attachment_path: this.attachment.path,
                      attachments: this.uploadedFiles
                    })
                  }
                ).catch(
                  ({ message }) => {
                    if (message && message?.errors) {
                      this.$refs.formDocument.setErrors(message.errors)
                    } else {
                      this.$refs.formDocument.setErrors({
                        attachments: ['An error occurred while uploading the attachments']
                      })
                    }
                  }
                ).finally(resolve)
              })
            }
          ).finally(() => {
            this.upload.progress = 0
            this.upload.loaded = 0
            this.upload.total = 0
            this.busy = false
          })
        }
      )
    },

    async previewDocument (_attachment, position) {
      this.preview.compress = false
      this.carousel = position

      this.$refs.modalDocuments.show()
    },

    async deleteUploaded (attachment) {
      this.swalConfirm({
        html: (
          `<div>
            <h6>Delete this Attachment?</h6>
            <h6 class="mt-2">
              <span class="font-weight-bolder text-dark">
                ${attachment.file_original_name}
              </span>
            </h6>
          </div>`
        ),
        confirmButtonText: 'Delete',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          this.busy = true

          return await SSharedAttachment.removeAttachments({ attachment_path: this.attachment.path, file_name: attachment.file_hashed_name }).then(
            ({ data: { message } }) => {
              this.$refs.formDocument.reset()

              this.swalSuccess('The file has been deleted').then(() => {
                const updatedFiles = this.uploadedFiles.filter(
                  file => file.file_hashed_name !== attachment.file_hashed_name
                )

                this.uploadedFiles = updatedFiles

                this.$emit('files:updated', {
                  attachment_path: this.attachment.path,
                  attachments: updatedFiles
                })
              })

              return message
            }
          ).finally(() => {
            this.busy = false
          })
        }
      })
    },

    async deleteAllUploaded () {
      this.swalConfirm({
        html: '<h6>Delete all the Uploaded Attachments?</h6>',
        confirmButtonText: 'Delete All',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          this.busy = true

          return await SSharedAttachment.removeAllAttachments({ attachment_path: this.attachment.path }).then(
            ({ data: { message } }) => {
              this.$refs.formDocument.reset()

              this.swalSuccess('All files have been deleted').then(() => {
                this.$emit('files:updated', {
                  attachment_path: this.attachment.path,
                  attachments: []
                })

                this.uploadedFiles = []
              })

              return message
            }
          ).finally(() => {
            this.busy = false
          })
        }
      })
    }
  }
}
</script>
<style lang="scss">
 .attachment-background {
  background-color: var(--iq-body-bg);
 }

 .dark .attachment-background {
  background-color: var(--iq-dark-body-bg);
 }

.carousel-control-next:hover, .carousel-control-prev:hover {
  background-color: #888;
}

img.attachment {
  width: 75px !important;
  height: 75px !important;
  object-fit: contain !important;
  border-radius: 8px;
  border: 1px solid gray !important;
  background-color: #aaa !important;
}

div.attachment {
  width: 75px !important;
  height: 75px !important;
  padding: 4px !important;
  position: relative !important;

  iframe.attachment-frame {
    width: 75px !important;
    height: 75px !important;
    border-radius: 8px;
    border: 1px solid gray !important;
    background-color: #aaa !important;
  }
}

.carousel-item {
  div.d-flex.justify-content-center.align-items-center {
    min-height: calc(100vh - 85px);
    max-height: calc(100vh - 85px);
    min-width: calc(100vw - 50px);
    max-width: calc(100vw - 50px);
    transition: transform 0.3s ease-in-out;

    iframe {
      min-height: calc(100vh - 85px);
      max-height: calc(100vh - 85px);
      min-width: 67vw;
      max-width: 67vw;
    }

    img.d-blocks {
      min-height: calc(100vh - 85px);
      max-height: calc(100vh - 85px);
      min-width: 67vw;
      max-width: 67vw;
      object-fit: contain;
      position: relative;
    }
  }
}

.progress > .progress-bar.bg-primary {
    background-image: linear-gradient(
      45deg,
    rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    );
    background-size: 1rem 1rem;
}
</style>
